import React, { useState, useEffect } from 'react'
import ReactDatePicker from 'react-datepicker'
import { subYears, addYears, addDays } from 'date-fns'
import OutsideClickHandler from 'react-outside-click-handler'
import DatePickerStyled from './DateInput.styles'
import styled, { css } from 'styled-components'
import { up, down } from 'styled-breakpoints'
import moment from 'moment'
import Arrow from '../../../assets/icons/left-arrow.svg'
import Calendar from '../../../assets/icons/calendar.svg'
import { pxToRem } from '../../../utils'
import { paragraph1CommonStyles } from '../../Typo'

const getBorderWidth = ({ error, disabled, focused }) => {
  if (disabled) {
    return '1px'
  }
  if (error && !disabled && !focused) {
    return '2px'
  } else if (!error && !focused && !disabled) {
    return '1px'
  }
  return '1px'
}

const getBorderColor = props => {
  const { error, disabled, focused, theme, value } = props
  if (disabled) {
    return theme.palette.grey5
  }
  if (!focused && !disabled && error) {
    return theme.palette.red1
  } else if (!error && !disabled && !focused && !value) {
    return theme.palette.grey3
  } else if (!error && !disabled && !focused && value) {
    return theme.palette.grey3
  }
  return theme.palette.grey3
}

//TODO: when extracting out DateInput need to create two variants: 1.with floating label 2.without label but has placeholder
const DatePicker = props => {
  const [selected, setSelectedDate] = useState(null)
  const [showDatePicker, setShowDatePicker] = useState(true)

  const onChange = date => {
    props.onBlur()
    props.onChange(date)
    setSelectedDate(date)
  }

  const formatDate = str => {
    const dateStr = str
      .split('/')
      .reverse()
      .join('/')
    return new Date(dateStr)
  }

  const onFocus = e => {
    if (props.onFocus) props.onFocus(e)
    e.target.blur()
  }

  const value = props.value
  useEffect(() => {
    if (!value) {
      setSelectedDate(new Date())
      return
    }
    typeof value === 'string' ? setSelectedDate(formatDate(value)) : setSelectedDate(value)
  }, [value])

  const customProps = {
    ...props,
    selected,
    minDate: subYears(new Date(), 100),
    maxDate: !props.allowFutureDates ? addDays(new Date(), 0) : addYears(new Date(), 18),
    onChange,
    onFocus
  }

  // NOTE: using react-date-picker lib's default triangle className, as it was not showing when using custom container
  const CalendarContainer = ({ children, className, arrowProps = {}, ...props }) => (
    <CalendarContainerStyled isVisible={showDatePicker}>
      <OutsideClickHandler
        onOutsideClick={() => {
          setShowDatePicker(false)
        }}
      >
        <CalendarContent className={className}>
          {/* <div className="react-datepicker__triangle" {...arrowProps} /> */}
          {children}
        </CalendarContent>
      </OutsideClickHandler>
    </CalendarContainerStyled>
  )

  return (
    <DatePickerStyled
      data-testid={'date-picker'}
      isVisible={showDatePicker}
      onClick={() => {
        setShowDatePicker(true)
      }}
    >
      <ReactDatePickerStyled
        {...customProps}
        showMonthDropdown={true}
        dateFormat={'dd/MM/yyyy'}
        showYearDropdown={true}
        dropdownMode={'select'}
        autoComplete={'off'}
        popperClassName="custom-wrapper"
        calendarContainer={CalendarContainer}
        popperPlacement="top"
        placeholderText="DD/MM/YYYY"
        popperModifiers={{
          computeStyle: {
            gpuAcceleration: false
          }
        }}
        previousMonthButtonLabel={<LeftArrow />}
        nextMonthButtonLabel={<RightArrow />}
        customInput={
          <InputContainer>
            <InputField
              type="text"
              placeholder="DD/MM/YYYY"
              value={value && moment(value, 'DD/MM/YYYY').format('DD/MM/YYYY')}
            />
            <StyledCalender />
          </InputContainer>
        }
      />
    </DatePickerStyled>
  )
}

const InputContainer = styled.div`
  display: -ms-flexbox; /* IE10 */
  display: flex;
  width: 100%;
`
const StyledCalender = styled(Calendar)`
  float: right;
`
const InputField = styled.input`
  width: 100%;
  outline: none;
  border: none;
  color: #4a4a4a;
  font-weight: bold;
  ::placeholder {
    font-weight: bolder;
  }
`

const arrowStyles = css`
  /* height: ${pxToRem(12)};
  width: ${pxToRem(15)}; */
  height: ${pxToRem(20)};
  width: ${pxToRem(44)};
  border: none; 
`

const LeftArrow = styled(Arrow)`
  ${arrowStyles};
`

const RightArrow = styled(LeftArrow)`
  ${arrowStyles};
  margin-top: ${pxToRem(8)};
  transform: rotate(180deg);
`

const CalendarContent = styled.div`
  ${down('sm')} {
    && {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
    }
  }
`

const CalendarContainerStyled = styled.div`
  display: ${props => (props.isVisible ? 'block' : 'none')};
  position: fixed;
  top: 30%;
  bottom: 10%;
  left: 25%;
  right: 10%;
  ${down('sm')} {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.4);
    transform: none;

    .react-datepicker__triangle {
      display: none;
    }
  }
`

const ReactDatePickerStyled = styled(ReactDatePicker)`
  width: 100%;
  ${paragraph1CommonStyles};
  background: transparent;
  color: ${props => props.theme.palette.grey1};
  font-weight: bold;

  ${props =>
    props.suffix &&
    css`
      padding-right: ${props => (props.type === 'number' ? `20%` : `15%`)};
    `};

  border: none;
  padding: 4px 0;

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-radius: 0;

  border-bottom: ${props => `${getBorderWidth(props)} solid ${getBorderColor(props)}`};

  :hover {
    border-bottom: 2px solid ${props => props.theme.palette.grey1};
  }
  :focus {
    outline: none;
    border-bottom: 2px solid ${props => props.theme.palette.grey1};
  }
  :disabled {
    color: ${props => props.theme.palette.grey6};
    border-bottom: 1px solid ${props => props.theme.palette.grey5};
    -webkit-text-fill-color: ${props => props.theme.palette.grey6};
  }

  ::placeholder {
    font-style: italic;
    color: ${props => props.theme.palette.grey4};
    font-weight: normal;
  }
`
export default DatePicker
